import { useEffect, useState } from "react";

import { tokenChipHue, tokenInitials } from "../utils/tokenIcon";

interface Props {
  /** Logo URL, or `null` to go straight to the chip. */
  src: string | null;
  /** Drives the chip's letters and colour when there is no image. */
  symbol: string;
  className?: string;
}

/**
 * A token's logo, falling back to a lettered chip.
 *
 * The chip is the COMMON path, not an error path. Only mints Cheddar custodies
 * have a curated `Mint.image`; every origin token reached through a swap has no
 * image at all, and fetching one would mean an external request on a payment
 * page. So the chip has to look deliberate rather than broken.
 *
 * It also covers a native mint whose `Mint.image` is missing or fails to load —
 * previously a bare grey dot.
 *
 * `referrerPolicy="no-referrer"` is kept even though the remaining URLs are our
 * own: an image host has no business knowing which merchant's checkout a payer
 * is on, whoever hosts it.
 */
export function TokenIcon({ src, symbol, className }: Props) {
  const [failed, setFailed] = useState(false);

  // A new src is a new chance to load; without this a single failure would
  // poison the slot for every token the payer scrolls past afterwards.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (src !== null && !failed) {
    return (
      <img
        src={src}
        alt=""
        className={className}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }

  const hue = tokenChipHue(symbol);
  return (
    <span
      aria-hidden="true"
      className={`ps-token-chip${className ? ` ${className}` : ""}`}
      style={{
        backgroundColor: `hsl(${hue} 62% 92%)`,
        color: `hsl(${hue} 55% 30%)`,
      }}
    >
      {tokenInitials(symbol)}
    </span>
  );
}
